<template>
  <v-layout align-start>
    <v-flex>
      <v-toolbar flat color="transparent">
        <v-toolbar-title>Encuestas</v-toolbar-title>
        <v-divider class="mx-2" inset vertical></v-divider>
        <v-spacer></v-spacer>
        <v-text-field
          class="text-xs-center"
          v-model="search"
          append-icon="search"
          label="Búsqueda"
          single-line
          hide-details
        ></v-text-field>
        <v-btn color="primary" dark class="ml-3" @click="openDialog()">
          <v-icon left>add</v-icon>Nueva Encuesta
        </v-btn>
      </v-toolbar>

      <v-data-table :headers="headers" :items="polls" :search="search" class="elevation-1">
        <template v-slot:item.active="{ item }">
          <v-chip small :color="item.active ? 'green' : 'grey'" dark>
            {{ item.active ? 'Activa' : 'Inactiva' }}
          </v-chip>
        </template>
        <template v-slot:item.options="{ item }">
          <div v-if="item.options && item.options.length" class="py-1">
            <v-chip x-small class="mr-1 mb-1" v-for="(opt, i) in item.options.slice(0, 3)" :key="i">
              {{ opt.text || opt }}
            </v-chip>
            <v-chip x-small class="mb-1" v-if="item.options.length > 3" color="grey lighten-2">
              +{{ item.options.length - 3 }}
            </v-chip>
          </div>
          <span v-else class="grey--text">—</span>
        </template>
        <template v-slot:item.expiresAt="{ item }">
          <span v-if="item.expiresAt">{{ formatDate(item.expiresAt) }}</span>
          <span v-else class="grey--text">Sin límite</span>
        </template>
        <template v-slot:item.createdAt="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>
        <template v-slot:item.opciones="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" color="blue darken-1" v-on="on" @click="openResults(item)">
                bar_chart
              </v-icon>
            </template>
            <span>Ver resultados</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" v-on="on" @click="openDialog(item)">edit</v-icon>
            </template>
            <span>Editar</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small class="mr-2" v-on="on"
                @click="item.active ? deactivatePoll(item._id) : activatePoll(item._id)">
                {{ item.active ? 'visibility_off' : 'visibility' }}
              </v-icon>
            </template>
            <span>{{ item.active ? 'Desactivar' : 'Activar' }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small color="red" v-on="on" @click="confirmDelete(item)">delete</v-icon>
            </template>
            <span>Eliminar</span>
          </v-tooltip>
        </template>
        <template v-slot:no-data>
          <div class="pa-4 text-center grey--text">No hay encuestas registradas</div>
        </template>
      </v-data-table>

      <!-- Dialog: Crear/Editar -->
      <v-dialog v-model="dialog" max-width="600" persistent>
        <v-card>
          <v-card-title class="headline primary white--text">
            {{ isEditing ? 'Editar Encuesta' : 'Nueva Encuesta' }}
          </v-card-title>
          <v-card-text class="pt-4">
            <v-text-field v-model="form.title" label="Pregunta *" outlined></v-text-field>
            <v-textarea v-model="form.description" label="Descripción" outlined rows="2"></v-textarea>
            <div class="subtitle-2 mb-2 font-weight-medium">Opciones de respuesta</div>
            <div
              v-for="(opt, i) in form.options"
              :key="i"
              class="d-flex align-center mb-2"
            >
              <v-text-field
                v-model="form.options[i]"
                :label="'Opción ' + (i + 1)"
                outlined
                dense
                hide-details
                class="mr-2"
              ></v-text-field>
              <v-btn icon small @click="form.options.splice(i, 1)" v-if="form.options.length > 2">
                <v-icon small color="red">close</v-icon>
              </v-btn>
            </div>
            <v-btn text small color="primary" @click="form.options.push('')">
              <v-icon left small>add</v-icon>Agregar opción
            </v-btn>

            <v-divider class="my-4"></v-divider>
            <div class="subtitle-2 mb-2 font-weight-medium">Disponibilidad (opcional)</div>
            <p class="caption grey--text mb-2">
              Define el cronómetro de la encuesta: desde cuándo y hasta cuándo estará disponible para votar. Si dejás los campos vacíos, queda disponible sin límite de tiempo (hasta que la desactives manualmente).
            </p>
            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.startsAt"
                  label="Disponible desde"
                  type="datetime-local"
                  outlined
                  dense
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.expiresAt"
                  label="Disponible hasta"
                  type="datetime-local"
                  outlined
                  dense
                  clearable
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeDialog()">Cancelar</v-btn>
            <v-btn
              color="primary"
              @click="save()"
              :disabled="!form.title || form.options.filter(o => o.trim()).length < 2"
            >
              {{ isEditing ? 'Guardar' : 'Crear' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog: Resultados -->
      <v-dialog v-model="resultsDialog" max-width="550">
        <v-card v-if="selectedPoll">
          <v-card-title class="headline primary white--text">
            Resultados — {{ selectedPoll.poll.title }}
            <v-spacer></v-spacer>
            <v-btn icon dark @click="resultsDialog = false"><v-icon>close</v-icon></v-btn>
          </v-card-title>
          <v-card-text class="pt-4">
            <div v-if="loadingResults" class="text-center pa-4 grey--text">Cargando resultados...</div>
            <template v-else>
              <div class="text-center mb-4">
                <span class="caption grey--text">Total de votos: </span>
                <span class="font-weight-bold">{{ selectedPoll.totalVotes }}</span>
              </div>
              <div v-if="selectedPoll.poll.options && selectedPoll.poll.options.length">
                <div
                  v-for="(opt, i) in selectedPoll.poll.options"
                  :key="i"
                  class="mb-4"
                >
                  <div class="d-flex justify-space-between mb-1">
                    <span class="body-2">{{ opt }}</span>
                    <span class="caption grey--text">
                      {{ selectedPoll.optionCounts[opt] || 0 }} votos
                      ({{ optionPercent(opt, selectedPoll) }}%)
                    </span>
                  </div>
                  <v-progress-linear
                    :value="optionPercent(opt, selectedPoll)"
                    :color="optionColor(i)"
                    height="22"
                    rounded
                  >
                    <span class="caption white--text font-weight-bold">
                      {{ optionPercent(opt, selectedPoll) }}%
                    </span>
                  </v-progress-linear>
                </div>
              </div>
              <div v-else class="text-center grey--text pa-4">
                Sin opciones registradas
              </div>
            </template>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="resultsDialog = false">Cerrar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import swal from 'sweetalert';

const OPTION_COLORS = ['blue', 'green', 'orange', 'purple', 'red', 'teal', 'indigo', 'pink'];

export default {
  data() {
    return {
      search: '',
      dialog: false,
      resultsDialog: false,
      isEditing: false,
      editingItem: null,
      selectedPoll: null,
      loadingResults: false,
      form: { title: '', description: '', options: ['', ''], startsAt: '', expiresAt: '' },
      headers: [
        { text: 'Opciones', value: 'opciones', sortable: false },
        { text: 'Pregunta', value: 'title', sortable: true },
        { text: 'Respuestas', value: 'options', sortable: false },
        { text: 'Estado', value: 'active', sortable: true },
        { text: 'Vence', value: 'expiresAt', sortable: true },
        { text: 'Fecha', value: 'createdAt', sortable: true },
      ],
    };
  },
  created() {
    this.getPolls();
  },
  computed: {
    ...mapState('pollNamespace', ['polls']),
  },
  methods: {
    ...mapActions('pollNamespace', [
      'getPolls',
      'savePoll',
      'updatePoll',
      'activatePoll',
      'deactivatePoll',
      'deletePoll',
      'getPollResults',
    ]),
    openDialog(item) {
      if (item) {
        this.isEditing = true;
        this.editingItem = item;
        this.form = {
          title: item.title,
          description: item.description || '',
          options: item.options ? item.options.map(o => o.text || o) : ['', ''],
          startsAt: this.toDatetimeLocal(item.startsAt),
          expiresAt: this.toDatetimeLocal(item.expiresAt),
        };
      } else {
        this.isEditing = false;
        this.editingItem = null;
        this.form = { title: '', description: '', options: ['', ''], startsAt: '', expiresAt: '' };
      }
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.form = { title: '', description: '', options: ['', ''], startsAt: '', expiresAt: '' };
    },
    toDatetimeLocal(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
    async openResults(poll) {
      this.resultsDialog = true;
      this.loadingResults = true;
      this.selectedPoll = { poll, totalVotes: 0, optionCounts: {} };
      const results = await this.getPollResults(poll._id);
      if (results) this.selectedPoll = results;
      this.loadingResults = false;
    },
    async save() {
      const data = {
        ...this.form,
        options: this.form.options.filter(o => o.trim()),
        startsAt: this.form.startsAt ? new Date(this.form.startsAt) : null,
        expiresAt: this.form.expiresAt ? new Date(this.form.expiresAt) : null,
      };
      if (this.isEditing) {
        await this.updatePoll({ ...data, _id: this.editingItem._id });
      } else {
        await this.savePoll(data);
      }
      this.closeDialog();
    },
    confirmDelete(item) {
      swal({
        title: '¿Estás seguro?',
        text: `Se eliminará la encuesta "${item.title}". Esta acción no se puede deshacer.`,
        icon: 'warning',
        buttons: ['Cancelar', 'Eliminar'],
        dangerMode: true,
      }).then((willDelete) => {
        if (willDelete) this.deletePoll(item._id);
      });
    },
    optionPercent(opt, selectedPoll) {
      const total = selectedPoll.totalVotes;
      if (!total) return 0;
      return Math.round(((selectedPoll.optionCounts[opt] || 0) / total) * 100);
    },
    optionColor(i) {
      return OPTION_COLORS[i % OPTION_COLORS.length];
    },
    formatDate(dateString) {
      if (!dateString) return '—';
      const date = new Date(dateString);
      return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    },
  },
};
</script>
